import { SliderOwnProps, Stack } from '@mui/material';
import { usePopupState } from 'material-ui-popup-state/hooks';
import { useAlertCameraPlayerState } from 'features/cameras/hooks/useAlertCameraPlayerState';
import { AlertCamera } from 'features/cameras/cameras.types';
import { useCallback } from 'react';
import { useCameraPlayerCommandPaletteActions } from 'hooks/commandPaletteActions';
import { CameraImage } from '../CameraImage';
import { CameraPreview } from '../CameraPreview';
import { CameraPlayerControls } from './CameraPlayerControls';
import { CameraReplayTimeMenu } from './CameraReplayTimeMenu';
import { CameraPlayerMobileFullScreenControls } from './CameraPlayerMobileFullScreenControls';
import { CameraPlayerControlCommonProps } from './CameraPlayerControl.types';
import { REPLAY_TIME_OPTIONS_SECONDS } from './CameraReplayTimeMenu.constants';
import { isMobileFullScreen } from '../../cameras.utils';

type CameraPlayerProps = {
  camera: AlertCamera;
  fullScreen?: boolean;
};

export const CameraPlayer = (props: CameraPlayerProps): JSX.Element => {
  const { camera, fullScreen = false } = props;
  const alertCameraPlayerState = useAlertCameraPlayerState(camera);
  const {
    playerStatus,
    timelapseFrameNumber,
    timelapseFramesLength,
    timelapseReplayTime,
    imageUrl,
    imageTimestamp,
    isLoading,
    isLoadingImage,
    setTimelapseFrameNumber,
    playTimelapse,
    pauseTimelapse,
    stopTimelapse,
    setTimelapseReplayTime,
  } = alertCameraPlayerState;
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'playTimeMenu',
  });

  const handleTogglePlay = (): void => {
    if (playerStatus === 'playingTimelapse') {
      pauseTimelapse();
      return;
    }
    playTimelapse();
  };

  const handleStopTimelapse = (): void => {
    stopTimelapse();
  };

  const handleSliderChange: SliderOwnProps<number>['onChange'] = (
    _,
    value,
  ): void => {
    pauseTimelapse();
    setTimelapseFrameNumber(value);
  };

  const mobileFullScreen = isMobileFullScreen(fullScreen);

  const cameraControlCommonProps: CameraPlayerControlCommonProps = {
    camera,
    imageTimestamp,
    isLoading,
    playerStatus,
    popupState,
    timelapseFramesLength,
    timelapseFrameNumber,
    timelapseReplayTime,
    handleSliderChange,
    handleStopTimelapse,
    handleTogglePlay,
  };

  const adjustReplay = useCallback(
    (currentValueSeconds: number, increase: boolean): number => {
      const index = REPLAY_TIME_OPTIONS_SECONDS.indexOf(currentValueSeconds);
      if (index === -1) {
        return REPLAY_TIME_OPTIONS_SECONDS[0];
      }

      if (increase) {
        return index < REPLAY_TIME_OPTIONS_SECONDS.length - 1
          ? REPLAY_TIME_OPTIONS_SECONDS[index + 1]
          : REPLAY_TIME_OPTIONS_SECONDS[index];
      }
      return index > 0
        ? REPLAY_TIME_OPTIONS_SECONDS[index - 1]
        : REPLAY_TIME_OPTIONS_SECONDS[index];
    },
    [],
  );

  useCameraPlayerCommandPaletteActions({
    alertCameraPlayerState,
    handleTogglePlay,
    handleStopTimelapse,
    adjustReplay,
  });

  return (
    <>
      <Stack
        spacing={0}
        sx={{
          width: mobileFullScreen ? '100%' : undefined,
          height: mobileFullScreen ? '100%' : undefined,
        }}
      >
        {playerStatus === 'streamingLive' ? (
          <CameraPreview
            url={imageUrl}
            loading={isLoading || isLoadingImage}
            fullScreen={fullScreen}
          />
        ) : (
          <CameraImage
            url={imageUrl}
            loading={isLoading || isLoadingImage}
            fullScreen={fullScreen}
          />
        )}

        {mobileFullScreen ? (
          <CameraPlayerMobileFullScreenControls {...cameraControlCommonProps} />
        ) : (
          <CameraPlayerControls
            {...cameraControlCommonProps}
            fullScreen={fullScreen}
          />
        )}
      </Stack>

      <CameraReplayTimeMenu
        camera={camera}
        isLoading={isLoading}
        popupState={popupState}
        setTimelapseReplayTime={setTimelapseReplayTime}
      />
    </>
  );
};
